/**
 * LabelForge API client — fetch helpers + WebSocket.
 */

// Import statique : BarcodeElement et lotCsv chargent déjà ce module, un import()
// ne le découpait pas (relevé par Jabir sur #306). bwip-js, lui, reste chargé à
// la demande par chargerBwipjs.
import { pngDuCodeBarres, messageCodeBarres } from '../utils/codeBarres';

export const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8090";


/**
 * Rend LISIBLE le `detail` d'une erreur d'API, quelle que soit sa forme.
 *
 * FastAPI en sert trois, et une seule est une chaîne :
 *   - `"message"` — une HTTPException que nous avons écrite ;
 *   - `[{ loc, msg, type }, …]` — un refus de validation Pydantic ;
 *   - `{ … }` — un objet, selon le gestionnaire.
 *
 * Passer les deux dernières à `new Error(...)` donne **`[object Object]`**, et
 * l'utilisateur voit ça au lieu du motif du refus. C'est arrivé le 2026-09-10 sur
 * l'enregistrement du jeton FDS : le message réel était masqué, donc la cause
 * aussi. Cinq autres appels portaient le même défaut.
 *
 * Le champ `loc` est repris parce qu'il nomme le champ fautif : « token : … »
 * vaut mieux qu'un message flottant. Les deux bornes que Pydantic peut opposer
 * ici sont traduites ; le reste est rendu tel quel, en anglais, ce qui est
 * toujours préférable à une forme illisible.
 */
export function messageDErreur(detail, defaut = "Erreur inattendue") {
  if (detail === null || detail === undefined || detail === "") return defaut;
  if (typeof detail === "string") return detail;

  const traduire = (msg) => {
    const m = String(msg || "");
    let r = /String should have at least (\d+) characters?/.exec(m);
    if (r) return `trop court : ${r[1]} caractères au minimum`;
    r = /String should have at most (\d+) characters?/.exec(m);
    if (r) return `trop long : ${r[1]} caractères au maximum`;
    if (/Field required/i.test(m)) return "champ obligatoire";
    return m;
  };
  const une = (e) => {
    if (typeof e === "string") return e;
    if (!e || typeof e !== "object") return "";
    const msg = traduire(e.msg ?? e.message ?? e.detail ?? "");
    // `loc` vaut ["body", "token"] : on ne garde que le nom du champ.
    const champ = Array.isArray(e.loc)
      ? e.loc.filter((x) => typeof x === "string" && x !== "body" && x !== "query").pop()
      : null;
    if (msg) return champ ? `${champ} : ${msg}` : msg;
    return "";
  };

  if (Array.isArray(detail)) {
    const lignes = detail.map(une).filter(Boolean);
    return lignes.length ? lignes.join(" · ") : defaut;
  }
  const seule = une(detail);
  if (seule) return seule;
  // Dernier recours : montrer la forme brute plutôt que « [object Object] ».
  try { return JSON.stringify(detail).slice(0, 300); } catch (_) { return defaut; }
}

/** Stored credentials — supports both JWT Bearer and legacy Basic Auth. */
let _authHeader = "";

/**
 * Set the Basic Auth credentials (legacy — used for backward compat).
 */
export function setCredentials(username, password) {
  _authHeader = "Basic " + btoa(`${username}:${password}`);
}

/**
 * Set JWT Bearer token (new auth system).
 * Pass a falsy value (null/''/undefined) to clear the header on logout.
 */
export function setBearerToken(token) {
  _authHeader = token ? ("Bearer " + token) : "";
}

/**
 * Clear all stored credentials. Called from useAuthStore.logout().
 */
export function clearAuth() {
  _authHeader = "";
}

/**
 * Check if credentials are currently set (JWT or Basic).
 */
export function hasCredentials() {
  if (_authHeader !== "") return true;
  // Check if JWT is in useAuthStore (in-memory or persisted refreshToken)
  try {
    const stored = JSON.parse(localStorage.getItem('labelforge-auth') || '{}');
    // accessToken may be in memory (not persisted), check refreshToken as proof of session
    if (stored?.state?.refreshToken && stored?.state?.isAuthenticated) {
      return true; // Token will be set via setBearerToken() when auth store hydrates
    }
  } catch (e) { /* ignore */ }
  return false;
}

/**
 * Get the current auth header value (Bearer or Basic).
 */
export function getAuthHeader() {
  if (!_authHeader) hasCredentials(); // Try to load from localStorage
  return _authHeader;
}

/**
 * Get the base64 token for WebSocket auth (works with both JWT and Basic).
 */
export function _getWsToken() {
  const header = getAuthHeader();
  if (header.startsWith("Bearer ")) return header.replace("Bearer ", "");
  return header.replace("Basic ", "");
}

/**
 * Fetch authentifié : pose le jeton, et sur 401 le RENOUVELLE puis rejoue une fois.
 *
 * C'est la « danse 401 » du fichier, extraite pour que TOUT appel authentifié en
 * bénéficie, y compris vers une URL absolue d'un autre service. Elle vivait
 * auparavant à l'intérieur d'`apiFetch`, donc seuls les appels au backend y avaient
 * droit : les exports, qui passent par des fetch écrits à la main, tombaient en 401
 * dès que le jeton d'accès avait passé ses 120 minutes — le service de rendu
 * répondait « Token invalide » et l'export était mort, sans que rien ne renouvelle.
 * Un après-midi de composition suffisait à y arriver.
 *
 * Renvoie la Response BRUTE : chaque appelant garde son propre traitement d'erreur.
 * Un en-tête Basic (legacy) n'est pas rejouable — il n'y a rien à renouveler.
 *
 * @param {string} url URL complète (backend ou service de rendu)
 * @param {RequestInit} options
 * @returns {Promise<Response>}
 */
export async function fetchAuthentifie(url, options = {}) {
  const headers = { ...options.headers };
  if (!headers.Authorization) headers.Authorization = getAuthHeader();

  let res = await fetch(url, { ...options, headers });

  if (res.status === 401 && headers.Authorization?.startsWith("Bearer ")) {
    try {
      // Import dynamique pour éviter la dépendance circulaire avec le store.
      const { useAuthStore } = await import('../store/useAuthStore');
      // refreshAccessToken() coalesce les appels concurrents et repose lui-même
      // l'en-tête du module via setBearerToken() — on relit donc getAuthHeader().
      const newToken = await useAuthStore.getState().refreshAccessToken();
      if (newToken) {
        headers.Authorization = "Bearer " + newToken;
        res = await fetch(url, { ...options, headers });
      }
    } catch (e) { /* refresh failed */ }
  }

  return res;
}

/**
 * Message d'un 401 QUI A SURVÉCU au renouvellement : la session est bel et bien
 * finie (jeton de rafraîchissement périmé ou révoqué), et il faut se reconnecter.
 *
 * Sans ça, les exports remontaient tels quels le « Token invalide » du service de
 * rendu — exact, mais illisible : l'utilisatrice comprend qu'elle a un problème de
 * fichier, pas de session, et recommence son export en boucle.
 */
const MESSAGE_SESSION_FINIE = "Session expirée — reconnectez-vous, puis relancez l'export. Votre travail reste en place.";

/**
 * Generic fetch wrapper with auth header injection.
 */
async function apiFetch(path, options = {}) {
  const url = `${API_URL}${path}`;
  const headers = {
    ...options.headers,
    Authorization: _authHeader,
  };

  if (options.body && typeof options.body === "object" && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(options.body);
  }

  let res;
  try {
    res = await fetchAuthentifie(url, { ...options, headers });
  } catch (networkErr) {
    // Erreur réseau (DNS, TCP, timeout, CORS, etc.) → on notifie le monitor
    // pour qu'il affiche le toast "Serveur instable" instantanément.
    window.dispatchEvent(new Event('labelforge:fetch-failed'));
    throw networkErr;
  }

  // 5XX = problème serveur → on notifie aussi pour déclencher le toast
  if (res.status >= 500) {
    window.dispatchEvent(new Event('labelforge:fetch-failed'));
  }

  if (res.status === 401) {
    throw new Error("Unauthorized — invalid credentials");
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API error ${res.status}: ${text}`);
  }

  if (res.status === 204) return null;

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json();
  }
  return res;
}

// ─── Jobs ─────────────────────────────────────────

export async function createJob(labelRequest) {
  return apiFetch("/api/jobs", { method: "POST", body: labelRequest });
}

export async function createBatchJobs(requests) {
  return apiFetch("/api/jobs/batch", { method: "POST", body: requests });
}

export async function listJobs(page = 1, perPage = 20, status = null) {
  let url = `/api/jobs?page=${page}&per_page=${perPage}`;
  if (status) url += `&status=${status}`;
  return apiFetch(url);
}

export async function getJob(jobId) {
  return apiFetch(`/api/jobs/${jobId}`);
}

export async function deleteJob(jobId) {
  return apiFetch(`/api/jobs/${jobId}`, { method: "DELETE" });
}

// ─── Labels (downloads) ──────────────────────────

export function getLabelUrl(jobId, format) {
  return `${API_URL}/api/labels/${jobId}/${format}`;
}

/**
 * Download a label file as a Blob.
 */
export async function downloadLabel(jobId, format) {
  const res = await apiFetch(`/api/labels/${jobId}/${format}`);
  return res.blob();
}

/**
 * Aplatit les gommes des calques image en vraies images à canal alpha.
 *
 * Indispensable côté SVG et WeasyPrint, qui ignorent `mask-image` : sans cette
 * étape, les pixels effacés à l'écran réapparaîtraient à l'impression. Le
 * module est chargé à la demande — un projet sans gomme ne paie rien.
 */
async function _flattenErased(elements) {
  if (!Array.isArray(elements) || !elements.some((e) => e && e.type === "image" && e.eraseMask)) {
    return elements;
  }
  const { flattenEraseMasks } = await import("../utils/eraseMask");
  return flattenEraseMasks(elements);
}

/**
 * Cuit les réglages d'image (flou, luminosité, contraste, niveaux, teinte,
 * saturation) dans les pixels, avant l'envoi.
 *
 * MÊME RÈGLE QUE LA GOMME, ET POUR LA MÊME RAISON : le chemin SVG et le chemin
 * WeasyPrint ne lisent pas un `filter` CSS, et le chemin Chromium le lirait mais
 * n'a plus à être cru sur parole. Un seul aplatissement, tous les chemins
 * d'accord.
 *
 * À APPELER AVANT `_flattenErased`, jamais après : à l'écran le `filter`
 * s'applique à l'image PUIS le masque la découpe. Cuire dans l'autre sens
 * flouterait le bord de la découpe au lieu de découper une image floutée.
 */
async function _flattenReglages(elements) {
  if (!Array.isArray(elements) || !elements.some((e) => e && e.type === "image" && e.adjust)) {
    return elements;
  }
  const { aplatirReglages } = await import("../utils/imageAdjust");
  return aplatirReglages(elements);
}

/**
 * Generate a PDF or SVG export from the frontend layout.
 */
export async function exportLabel(exportData, format) {
  exportData.format = format;

  if (exportData.elements) {
    // Codes-barres → images, DESSINÉES depuis les données de chaque calque.
    // Recopier le canvas affiché (`barcode-canvas-<id>`) donnait la valeur de
    // l'écran, pas celle exportée : un lot CSV sortait le même code-barres dans
    // tous ses PDF (relevé par Jabir, 2026-10-09). Et un code-barres invalide
    // partait vide, ou avec son dernier dessin valide ; il arrête désormais
    // l'export, avec la raison.
    exportData.elements = await Promise.all(exportData.elements.map(async (el) => {
      // Un calque masqué part tel quel : le serveur le saute (build_html.py,
      // routes/export.py). Invalide, il ne doit pas bloquer un export qui ne
      // l'imprimera pas (relevé par Jabir sur #306).
      if (el.type !== 'barcode' || el.hidden) return el;
      try {
        return { ...el, type: 'image', url: await pngDuCodeBarres(el) };
      } catch (e) {
        throw new Error(`le code-barres « ${el.text || el.name || el.id} » ne peut pas être dessiné (${messageCodeBarres(e)}).`);
      }
    }));

    // Convert blob/object URLs to base64 for images
    exportData.elements = await Promise.all(exportData.elements.map(async (el) => {
      if (el.type === 'image' && el.url && !el.url.startsWith('data:')) {
        try {
          const res = await fetch(el.url);
          const blob = await res.blob();
          const b64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          return { ...el, url: b64 };
        } catch (e) {
          console.warn('Could not convert image to base64:', el.url);
        }
      }
      return el;
    }));

    // Réglages puis gomme → image aplatie (ni le moteur SVG ni WeasyPrint ne
    // lisent un `filter` ou un masque CSS). L'ordre compte, cf. _flattenReglages.
    exportData.elements = await _flattenReglages(exportData.elements);
    exportData.elements = await _flattenErased(exportData.elements);
  }

  const url = `${API_URL}/api/export`;
  const headers = {
    "Content-Type": "application/json",
    Authorization: getAuthHeader(),
  };
  const res = await fetchAuthentifie(url, { method: "POST", headers, body: JSON.stringify(exportData) });
  if (!res.ok) {
    if (res.status === 401) throw new Error(MESSAGE_SESSION_FINIE);
    const err = await res.text();
    throw new Error(`Export error: ${err}`);
  }
  return res.blob();
}

/**
 * URL du service de rendu raster (headless Chrome sur /print).
 * - En dev (vite) : service POC local sur :8099.
 * - En prod : endpoint backend /api/export (à brancher au déploiement).
 * Surchargeable via VITE_RENDER_URL.
 */
const RENDER_URL = import.meta.env.VITE_RENDER_URL
  || (import.meta.env.DEV
    ? "http://127.0.0.1:8099/render"
    : "https://labelforge-render-1095335246884.europe-west1.run.app/render");

// URL S3 d'une image de galerie : .../gallery/originals/<image_id>.png
// (l'UUID du nom de fichier EST l'image_id en base — cf. backend gallery.py).
const S3_GALLERY_RE = /\/gallery\/originals\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\.png/i;

/**
 * Convertit une URL d'image en base64 si elle n'est pas transférable telle quelle.
 *
 * Cas critique — images de galerie S3 : l'URL stockée dans le projet est une URL
 * PRÉSIGNÉE qui expire (1 h). L'éditeur l'affiche depuis le cache navigateur même
 * après expiration, mais le service de rendu (Chrome neuf, sans cache) ne peut plus
 * la charger → image cassée à l'export. Solution : passer par l'endpoint backend
 * /api/me/gallery/{id}/base64 (credentials S3 côté serveur, contourne expiration +
 * CORS) pour récupérer une version fraîche en base64.
 *
 * Les blob:/object URLs (locales à l'éditeur) et autres https sont inlinés par
 * fetch direct (best-effort).
 */
async function _inlineImage(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string" || rawUrl.startsWith("data:")) return rawUrl;

  // 1) Image de galerie S3 → endpoint base64 du backend (robuste, pas d'expiration/CORS).
  const m = rawUrl.match(S3_GALLERY_RE);
  if (m) {
    try {
      const res = await fetchAuthentifie(`${API_URL}/api/me/gallery/${m[1]}/base64`, {});
      if (res.ok) {
        const j = await res.json();
        if (j && j.base64) return j.base64;
      }
    } catch (_) { /* tombe sur le fetch direct ci-dessous */ }
  }

  // 2) Fallback : fetch direct (blob:, object URLs, autres https).
  try {
    const res = await fetch(rawUrl);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(rawUrl);
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return rawUrl; // garde l'URL si tout échoue
  }
}

/**
 * Export raster via le service de rendu : projet + options → Blob (PDF ou PNG).
 * Le projet est { canvas, elements, aiImage } (getProjectData). On inline les
 * images non-transférables avant l'envoi. Les barcodes/QR sont rendus par
 * /print (BarcodeElement) — pas de conversion nécessaire ici.
 *
 * @param {{project:object, format:'pdf'|'png', dpi:number, includeBleed:boolean, fonts?:Array}} opts
 * @returns {Promise<Blob>}
 */
export async function renderLabelExport({ project, format = "pdf", dpi = 300, includeBleed = false, fonts = null, engine = "raster", gradientText = "raster" }) {
  const proj = {
    canvas: project.canvas,
    elements: JSON.parse(JSON.stringify(project.elements || [])),
    aiImage: project.aiImage ? { ...project.aiImage } : null,
  };
  proj.elements = await Promise.all(proj.elements.map(async (el) => {
    if (el.type === "image" && el.url) return { ...el, url: await _inlineImage(el.url) };
    return el;
  }));
  // Réglages d'image, puis gomme : aplatir AVANT l'envoi. Le service de rendu
  // passe par Chromium et saurait interpréter le `filter` comme le masque CSS,
  // mais les aplatir ici met tous les chemins d'export sur le même pied et
  // supprime toute dépendance au moteur. L'ordre compte, cf. _flattenReglages.
  proj.elements = await _flattenReglages(proj.elements);
  proj.elements = await _flattenErased(proj.elements);
  if (proj.aiImage && proj.aiImage.url) {
    proj.aiImage.url = await _inlineImage(proj.aiImage.url);
  }

  const res = await fetchAuthentifie(RENDER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ project: proj, format, dpi, includeBleed, fonts, engine, gradientText }),
  });
  if (!res.ok) {
    let msg = res.status === 401 ? MESSAGE_SESSION_FINIE : `Export échoué (HTTP ${res.status})`;
    try { if (res.status !== 401) { const j = await res.json(); if (j.detail) msg = messageDErreur(j.detail, msg); } }
    catch (_) { try { msg = (await res.text()) || msg; } catch (__) { /* ignore */ } }
    throw new Error(msg);
  }
  return res.blob();
}

/**
 * Post-traitement PRÊT-PRESSE (moteur Print Pro du backend) : transforme le
 * PDF RGB du service de rendu en PDF CMJN normé — profil ICC (FOGRA), norme
 * PDF/X, TrimBox/BleedBox, traits de coupe vectoriels. Le rapport de
 * couverture d'encre arrive dans le header X-Print-Report (base64 JSON).
 * Renvoie { blob, report }.
 */
export async function finishPrintPro(pdfBlob, options) {
  const fd = new FormData();
  fd.append("file", pdfBlob, "render.pdf");
  fd.append("options", JSON.stringify(options || {}));
  const res = await fetchAuthentifie(`${API_URL}/api/export/print-finish`, {
    method: "POST",
    body: fd,
  });
  if (!res.ok) {
    // Sans norme (préréglage RVB), il n'y a pas de conversion : seule la découpe est posée.
    const etape = options?.standard === 'none' ? 'Pose de la découpe en ton direct échouée' : 'Conversion prêt-presse échouée';
    let msg = res.status === 401 ? MESSAGE_SESSION_FINIE : `${etape} (HTTP ${res.status})`;
    try { if (res.status !== 401) { const j = await res.json(); if (j.detail) msg = messageDErreur(j.detail, msg); } }
    catch (_) { try { msg = (await res.text()) || msg; } catch (__) { /* ignore */ } }
    throw new Error(msg);
  }
  let report = null;
  try {
    const h = res.headers.get("X-Print-Report");
    if (h) report = JSON.parse(atob(h));
  } catch (_) { /* rapport optionnel — l'export reste valide sans lui */ }
  return { blob: await res.blob(), report };
}

// ─── Health & Settings ─────────────────────────────────
export async function checkHealth() {
  const res = await fetch(`${API_URL}/api/health`);
  return res.json();
}

export async function getWorkflowTemplate() {
  const url = `${API_URL}/api/comfy/workflow`;
  const headers = {
    Authorization: getAuthHeader(),
  };
  const res = await fetchAuthentifie(url, { method: "GET", headers });
  if (!res.ok) {
    throw new Error(`Failed to fetch workflow template: ${await res.text()}`);
  }
  return res.json();
}

// ─── WebSocket ───────────────────────────────────

/**
 * Open a WebSocket to stream job progress.
 * Returns the WebSocket instance.
 */
export function connectJobSocket(jobId) {
  const wsBase = API_URL.replace(/^http/, "ws");
  const token = _getWsToken();
  return new WebSocket(`${wsBase}/api/ws/jobs/${jobId}?token=${encodeURIComponent(token)}`);
}
